import { inject, observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../t';
import { useParams } from 'react-router-dom';
import CopyNames from '../../../components/CopyNames';
import Empty from '../../../components/Empty';
import ImageDialog from '../../../components/ImageDialog';
import LocalSperater from '../../../components/LocalSperater';
import withAuth from '../../../HOC/withAuth';
import http from '../../../http';
import FileUploader from '../FileUpload';
import StepShell from '../StepShell';
import C from '../../../colors';
import { getPaymentStatus, hasOrderSelections, isPaymentPaid, paymentStatusMeta } from '../../../orderStatus';
import { getPictureThumbnailUrl } from '../../../pictureUrls';
import {
  trackDuplicateDetected,
  trackOrderScanCompleted,
  trackOrderScanStarted,
  trackQualityDeletion,
  trackQualityFailure,
  trackQualityFilterViewed,
  trackQualityResult,
} from '../../../photoQuality/analytics';
import { groupNearDuplicates } from '../../../photoQuality/metrics';
import { downloadPictureForAnalysis, summarizeOrderQuality } from '../../../photoQuality/orderScanner';
import { enqueuePhotoQualityAnalysis } from '../../../photoQuality/qualityQueue';
import { clusterFaces, serializePersonGroups } from '../../../photoPeople/clustering';
import { consumePersonScan, loadPersonGroups, savePersonGroups } from '../../../photoPeople/persistence';
import { scanPicturePeople } from '../../../photoPeople/personScanner';

function Icon({ name, size = 14 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'search') return <svg viewBox="0 0 24 24" {...s}><circle cx="11" cy="11" r="6"/><path d="m20 20-4-4"/></svg>;
  return null;
}

function isAiQualityBrowserSupported() {
  if (typeof navigator === 'undefined') return true;
  return /Chrome\//.test(navigator.userAgent) || /Edg\//.test(navigator.userAgent);
}

const TAB_OPTS = [['all','全部']];
const DENSITIES = [4, 6, 8];
const CLIENT_COLORS = ['#F777B0','#D9883A','#6B8FB8','#3F8F5F','#8B6FB8','#D94F92','#C8782A','#4A7FA8'];

function DensityBtn({ cols, v, setV }) {
  const on = cols === v;
  const g = v === 4 ? 2 : v === 6 ? 3 : 4;
  return (
    <button onClick={() => setV(v)} style={{
      width: 28, height: 28, border: 'none', borderRadius: 5,
      background: on ? C.ink : 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${g}, 1fr)`, gap: 1.5, width: 14, height: 14 }}>
        {Array.from({ length: g * g }).map((_, i) => (
          <div key={i} style={{ background: on ? '#fff' : C.ink3, borderRadius: 0.5 }}/>
        ))}
      </div>
    </button>
  );
}

// ── ClientSelectorStrip ───────────────────────────────────────────

function ClientSelectorStrip({ selections, activeId, onSelect, onAggregate, isAggregate, aggregateCount, photoLimit, claimed, selectedPictures, selectionCustomerName, pictures }) {
  const { t } = useTranslation();
  const [showRecords, setShowRecords] = useState(false);

  return (
    <div style={{
      padding: '14px 36px',
      background: C.paper, borderBottom: `1px solid ${C.hair}`,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <div style={{
        fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4,
        color: C.ink3, textTransform: 'uppercase', fontWeight: 600, marginRight: 4,
      }}>选片人</div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {selections.map((sel, i) => (
          <ClientPill key={sel.id} client={sel}
            color={CLIENT_COLORS[i % CLIENT_COLORS.length]}
            active={!isAggregate && activeId === sel.id}
            count={sel.pictureIds?.length || 0} limit={photoLimit}
            onClick={() => onSelect(sel.id)}
          />
        ))}
      </div>

      <span style={{ width: 1, height: 24, background: C.hair2, margin: '0 4px' }}/>

      {/* 全员聚合 */}
      <button onClick={onAggregate} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '5px 12px 5px 8px', borderRadius: 22,
        background: isAggregate ? C.ink : '#fff',
        border: `1px solid ${isAggregate ? C.ink : C.hair2}`,
        color: isAggregate ? '#fff' : C.ink,
        fontFamily: C.sans, cursor: 'pointer', transition: 'all .15s',
      }}>
        <span style={{ display: 'inline-flex', position: 'relative', width: 20, height: 18 }}>
          {selections.slice(0, 3).map((c, i) => (
            <span key={c.id} style={{
              position: 'absolute', left: i * 5, top: 0,
              width: 14, height: 14, borderRadius: 7,
              background: CLIENT_COLORS[i % CLIENT_COLORS.length],
              border: `1.5px solid ${isAggregate ? C.ink : '#fff'}`,
            }}/>
          ))}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>全员聚合</span>
        <span style={{
          fontFamily: C.mono, fontSize: 11, fontVariantNumeric: 'tabular-nums',
          color: isAggregate ? 'rgba(255,255,255,.7)' : C.ink3,
        }}>{aggregateCount}</span>
      </button>

      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
        <button onClick={() => setShowRecords(true)} style={utilBtnStyle}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>☰</span> 选图记录
        </button>
        {claimed && <CopyNames pictures={selectedPictures}/>}
        {claimed && <LocalSperater dirname={selectionCustomerName} pictures={selectedPictures}/>}
      </div>

      {showRecords && <RecordsModal selections={selections} pictures={pictures} onSelect={onSelect} onClose={() => setShowRecords(false)}/>}
    </div>
  );
}

function RecordsModal({ selections, pictures, onSelect, onClose }) {
  const { t } = useTranslation();
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(26,22,20,.4)',
      display: 'grid', placeItems: 'center', zIndex: 9999,
      backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 600, maxHeight: '80vh',
        background: C.paper, border: `1px solid ${C.hair}`,
        borderRadius: 14, overflow: 'auto',
        boxShadow: '0 24px 56px -16px rgba(26,22,20,.32)',
      }}>
        <div style={{ padding: '20px 24px 12px', borderBottom: `1px solid ${C.hair}` }}>
          <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, color: C.brand2, textTransform: 'uppercase', fontWeight: 600 }}>Selection Records</div>
          <h3 style={{ margin: '4px 0 0', fontFamily: C.serif, fontSize: 26, fontWeight: 400 }}>{t("Selections Logs")}</h3>
        </div>
        <div style={{ padding: '8px 12px' }}>
          {selections.map((sel, i) => {
            const count = sel.pictureIds?.length || 0;
            const color = CLIENT_COLORS[i % CLIENT_COLORS.length];
            const selPics = (sel.pictureIds || []).map(pid => pictures?.find(p => p.id === pid)).filter(Boolean);
            const recent = selPics.slice(0, 4);
            return (
              <div key={sel.id} style={{
                display: 'grid', gridTemplateColumns: '36px 1fr auto 80px auto', gap: 14,
                alignItems: 'center', padding: '12px 14px', borderRadius: 10, border: 'none',
                background: 'transparent', textAlign: 'left',
              }}>
                <span style={{ width: 36, height: 36, borderRadius: 18, background: color, color: '#fff', display: 'grid', placeItems: 'center', fontFamily: C.serif, fontSize: 18 }}>{sel.customerName?.charAt(0)}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{sel.customerName}</div>
                  <div style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, marginTop: 2 }}>
                    {paymentStatusMeta(sel).label}{sel.createdAt ? ` · ${new Date(sel.createdAt).toLocaleDateString('zh-CN')}` : ''}
                  </div>
                </div>
                {/* Recent photo thumbnails */}
                <div style={{ display: 'flex', gap: 3 }}>
                  {recent.map(p => (
                    <div key={p.id} style={{
                      width: 22, height: 22, borderRadius: 3,
                      backgroundImage: `url(${getPictureThumbnailUrl(p)})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.06)',
                    }}/>
                  ))}
                  {selPics.length > 4 && (
                    <span style={{ fontSize: 10, color: C.ink3, alignSelf: 'center' }}>+{selPics.length - 4}</span>
                  )}
                </div>
                <span style={{ fontFamily: C.serif, fontSize: 20, color: C.ink, fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'right' }}>{count}</span>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => { onSelect(sel.id); onClose(); }} style={{
                    padding: '4px 10px', borderRadius: 5, border: `1px solid ${C.hair2}`,
                    background: '#fff', color: C.ink, fontFamily: C.sans, fontSize: 11, cursor: 'pointer',
                  }}>查看</button>
                  <button onClick={() => {
                    if (window.confirm(t("Are you sure you want to delete this selection record?"))) {
                      http.delete(`/user/selections/${sel.id}`).then(() => onClose());
                    }
                  }} style={{
                    padding: '4px 8px', borderRadius: 5, border: `1px solid transparent`,
                    background: 'transparent', color: '#A53636', fontFamily: C.sans, fontSize: 11, cursor: 'pointer',
                  }}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.hair}`, textAlign: 'right' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, border: `1px solid ${C.hair2}`, background: '#fff', cursor: 'pointer', fontFamily: C.sans, fontSize: 13 }}>{t("Close")}</button>
        </div>
      </div>
    </div>
  );
}

function ClientPill({ client, color, active, count, limit, onClick }) {
  const initial = client.customerName?.charAt(0) || '?';
  const reachedLimit = limit && count >= limit;
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '4px 12px 4px 4px', borderRadius: 22,
      background: active ? '#fff' : 'transparent',
      border: `1px solid ${active ? color : C.hair2}`,
      boxShadow: active ? `0 0 0 2px ${color}26, 0 4px 14px -8px ${color}66` : 'none',
      color: C.ink, fontFamily: C.sans, cursor: 'pointer',
      transition: 'all .15s', whiteSpace: 'nowrap',
    }}>
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <span style={{ width: 26, height: 26, borderRadius: 13, background: color, color: '#fff', display: 'grid', placeItems: 'center', fontFamily: C.serif, fontSize: 14, lineHeight: 1 }}>{initial}</span>
        <span style={{ position: 'absolute', right: -1, bottom: -1, width: 8, height: 8, borderRadius: 4, background: isPaymentPaid(client) ? '#3F8F5F' : C.ink3, border: '1.5px solid #fff' }}/>
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{client.customerName}</span>
        <span style={{ fontFamily: C.mono, fontSize: 11, color: reachedLimit ? '#3F8F5F' : active ? color : C.ink3, fontVariantNumeric: 'tabular-nums', fontWeight: active ? 600 : 500 }}>
          {count}<span style={{ color: C.ink3, fontWeight: 400 }}>/{limit}</span>
        </span>
      </span>
    </button>
  );
}

const utilBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 10px', borderRadius: 6,
  background: 'transparent', border: '1px solid transparent',
  color: C.ink2, fontFamily: C.sans, fontSize: 12, cursor: 'pointer',
};

// ── AggregateBadge ────────────────────────────────────────────────

function AggregateBadge({ selectedBy }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{
        position: 'absolute', top: 6, right: 6,
        ...(expanded ? { bottom: 6 } : {}),
        display: 'flex', alignItems: expanded ? 'stretch' : 'center', gap: 6,
        padding: expanded ? '6px 10px' : '3px 7px 3px 5px',
        background: 'rgba(0,0,0,.55)', borderRadius: expanded ? 10 : 12,
        color: '#fff', zIndex: 8,
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        transition: 'all .15s',
        maxWidth: expanded ? '90%' : undefined,
        cursor: 'default',
      }}>
      {!expanded ? (
        <>
          <div style={{ display: 'flex', marginRight: 4 }}>
            {selectedBy.slice(0, 3).map((c, i) => (
              <span key={c.id} style={{
                width: 14, height: 14, borderRadius: 7,
                background: c.color, marginLeft: i === 0 ? 0 : -4,
                border: '1.5px solid rgba(0,0,0,.55)',
                display: 'grid', placeItems: 'center',
                fontFamily: C.serif, fontSize: 9, color: '#fff', lineHeight: 1,
              }}>{c.initial}</span>
            ))}
          </div>
          <span style={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{selectedBy.length}</span>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, maxHeight: 180, overflowY: 'auto' }}>
          {selectedBy.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <span style={{
                width: 14, height: 14, borderRadius: 7, flexShrink: 0,
                background: c.color,
                border: '1.5px solid rgba(255,255,255,.3)',
              }}/>
              <span style={{ fontSize: 11, fontFamily: C.sans, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.customerName}
              </span>
              {isPaymentPaid(c) && (
                <span style={{ fontSize: 9, color: '#7FC99A', flexShrink: 0 }}>已付</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PhotoTile ──────────────────────────────────────────────────────

const PhotoTile = observer(({ orderId, albumId, picture, multiMode, isCover, pictureStore, albumClaimed, cols, aggregate, selectedBy, coverMode, pendingCover, onCoverSelect, qualityResult }) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);

  // Read the observable array directly. Memoizing by the array reference leaves
  // this stale when MobX mutates the same observable array in place.
  const multiSelected = pictureStore?.multiSelections?.some(
    pictureId => String(pictureId) === String(picture.id)
  ) || false;

  const isSelected = false; // selection state belongs in step5, not step3

  const handleClick = () => {
    if (isUploading) return;
    if (aggregate) return;
    if (coverMode) {
      onCoverSelect?.(picture.id);
    } else if (multiMode) {
      pictureStore.toggleMultiSelect(picture.id);
    } else {
      pictureStore.setCurrentPic(picture);
    }
  };

  const accent = selectedBy?.[0]?.color || C.brand;
  const isPendingCover = coverMode && pendingCover === picture.id;
  const isUploading = typeof picture.progress === 'number' && !picture.publicUrl;
  const progress = isUploading ? Math.max(0, Math.min(100, picture.progress)) : 100;
  const uploadLabel = picture.progressMessage || (progress <= 0 ? '处理中' : progress >= 100 ? '完成中' : '上传中');

  return (
    <div
      data-testid={isUploading ? 'photo-upload-progress' : `photo-tile-${picture.id}`}
      data-cover={!isUploading && isCover ? 'true' : 'false'}
      data-multi-selected={!isUploading && multiMode && multiSelected ? 'true' : 'false'}
      onClick={handleClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', aspectRatio: '4/3', border: 'none', padding: 0,
        borderRadius: 4, overflow: 'hidden', background: C.paper2,
        boxShadow: isPendingCover
          ? `0 0 0 3px ${C.brand}, 0 8px 24px -10px rgba(247,119,176,.6)`
          : multiSelected
            ? `0 0 0 3px ${C.brand}, 0 8px 24px -12px ${C.brand}A6`
            : '0 1px 2px rgba(0,0,0,.06)',
        outline: 'none',
        transition: 'box-shadow .15s, transform .1s',
        transform: hover && !coverMode ? 'scale(1.02)' : 'scale(1)',
        cursor: isUploading ? 'default' : coverMode ? 'pointer' : aggregate && !multiMode ? 'default' : 'pointer',
      }}>
      {isUploading ? (
        <>
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(110deg, ${C.paper2} 8%, #fff 18%, ${C.paper2} 33%)`,
            backgroundSize: '200% 100%',
            animation: 'imgShimmer 1.2s linear infinite',
          }}/>
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 16, textAlign: 'center' }}>
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 10 }}>{uploadLabel} · {progress}%</div>
              <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,.72)', overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)' }}>
                <div style={{ width: `${progress}%`, height: '100%', borderRadius: 999, background: C.brand, transition: 'width .18s ease' }}/>
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: C.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {picture.originalFileName || picture.fileName || '准备上传'}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <img src={getPictureThumbnailUrl(picture)} alt={picture.originalFileName || picture.fileName}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>
          {!coverMode && <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 35% 30%, rgba(255,255,255,.25), transparent 55%)', pointerEvents: 'none' }}/>}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '18px 7px 5px', background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,.68))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,.85)', fontFamily: 'ui-monospace,monospace', letterSpacing: 0.2, zIndex: 1, opacity: 1}}>
            {picture.originalFileName || picture.fileName}
          </div>
        </>
      )}

      {/* Tag stripe */}
      {!isUploading && !coverMode && picture.tags?.length > 0 && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: picture.tags[0]?.color || C.brand, zIndex: 1 }}/>
      )}

      {!isUploading && !coverMode && qualityResult && (
        <AiQualityBadge result={qualityResult} pictureId={picture.id}/>
      )}

      {/* Cover ribbon — always show in cover mode, only on current cover */}
      {!isUploading && isCover && (
        <div style={{ position: 'absolute', left: -22, top: 6, width: 80, textAlign: 'center', color: '#fff', background: C.brand, fontSize: 10, fontWeight: 600, transform: 'rotate(-45deg)', zIndex: 10, padding: '2px 0', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}>{t('Cover')}</div>
      )}


      {/* Multi-select overlay */}
      {!isUploading && !coverMode && multiMode && multiSelected && (
        <div style={{ position: 'absolute', inset: 0, background: `${C.brand}29`, zIndex: 4, pointerEvents: 'none' }}/>
      )}

      {/* Multi-select checkbox */}
      {!isUploading && !coverMode && multiMode && (
        <div data-testid={`photo-multi-checkbox-${picture.id}`} style={{
          position: 'absolute', top: 7, right: 7, zIndex: 5,
          width: 20, height: 20, borderRadius: 5,
          background: multiSelected ? C.brand : 'rgba(255,255,255,.94)',
          border: multiSelected ? `1.5px solid ${C.brand}` : `1.5px solid rgba(61,54,50,.28)`,
          color: '#fff', display: 'grid', placeItems: 'center',
          fontSize: 12, fontWeight: 700,
          boxShadow: multiSelected
            ? `0 4px 12px ${C.brand}6B`
            : '0 2px 7px rgba(26,22,20,.16)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        }}>
          {multiSelected ? '✓' : ''}
        </div>
      )}

      {/* Pending cover badge (cover mode) */}
      {!isUploading && isPendingCover && (
        <div style={{
          position: 'absolute', inset: 0,
          border: `3px solid ${C.brand}`, borderRadius: 4,
          display: 'grid', placeItems: 'center',
          background: `${C.brand}18`, zIndex: 8, pointerEvents: 'none',
        }}>
          <div style={{
            padding: '6px 14px', borderRadius: 6,
            background: C.brand, color: '#fff',
            fontFamily: C.sans, fontSize: 13, fontWeight: 600,
          }}>选为封面</div>
        </div>
      )}

      {/* Hover delete button */}
      {!isUploading && !multiMode && !aggregate && !coverMode && hover && (
        <button onClick={(e) => {
          e.stopPropagation();
          if (window.confirm(t('Are you sure you want to delete those pictures?'))) {
            if (qualityResult) trackQualityDeletion([qualityResult]);
            pictureStore.deletePictrus(orderId, [picture.id]);
          }
        }}
          title={t('Delete')}
          style={{
            position: 'absolute', bottom: 6, right: 6, zIndex: 9,
            ...hoverIconBtn,
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1.2 13a1.5 1.5 0 0 0 1.5 1.4h6.6a1.5 1.5 0 0 0 1.5-1.4L18 7"/></svg>
        </button>
      )}
    </div>
  );
});

function getAiQualityIssue(result) {
  if (!result) return null;
  if (result.faceAnalysis?.closedEyeFaces > 0) return { key: 'closed', label: `${result.faceAnalysis.closedEyeFaces} 人闭眼`, background: '#A53636' };
  if (result.sharpness?.isBlurry) return { key: 'blur', label: '疑似模糊', background: '#A53636' };
  if (result.exposure?.issue) return { key: 'exposure', label: result.exposure.issue === 'underexposed' ? '曝光不足' : '高光溢出', background: '#B17120' };
  if (result.isDuplicateAlternative) return { key: 'duplicate', label: '近似备选', background: '#7652A8' };
  if (result.faceAnalysis?.status === 'unavailable') return { key: 'partial', label: 'AI 部分完成', background: '#8A8079' };
  return { key: 'ok', label: 'AI 已检查', background: '#3F8F5F' };
}

function AiQualityBadge({ result, pictureId }) {
  const issue = getAiQualityIssue(result);
  return (
    <div data-testid={`photo-ai-quality-${pictureId}`} data-ai-issue={issue.key} style={{
      position: 'absolute', left: 7, top: 7, zIndex: 7,
      padding: '4px 7px', borderRadius: 6,
      background: issue.background, color: '#fff',
      fontFamily: C.sans, fontSize: 10, fontWeight: 600,
      boxShadow: '0 3px 9px rgba(0,0,0,.2)',
    }}>{issue.label}</div>
  );
}

const hoverIconBtn = {
  width: 30, height: 30, borderRadius: 8,
  background: 'rgba(0,0,0,.45)', border: 'none',
  color: '#fff', cursor: 'pointer',
  display: 'grid', placeItems: 'center',
  backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};

function UploadStatus({ uploadedCount, batch, onRetry }) {
  const batchPercent = batch?.pictureNum
    ? Math.round(((batch.completedFileCount || 0) / batch.pictureNum) * 100)
    : 0;
  const hasFailures = (batch?.failFileCount || 0) > 0;

  return (
    <section data-testid="upload-status" style={{
      padding: '14px 36px 16px', background: '#fff', borderBottom: `1px solid ${C.hair}`,
      display: 'grid', gridTemplateColumns: batch ? 'minmax(180px, .45fr) minmax(300px, 1fr)' : '1fr',
      gap: 28, flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>图库照片</span>
        <strong data-testid="upload-overall-count" style={{ fontFamily: C.mono, fontSize: 22, lineHeight: 1, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
          {uploadedCount}<span style={{ marginLeft: 4, color: C.ink3, fontSize: 12, fontWeight: 400 }}>张</span>
        </strong>
      </div>

      {batch && (
        <div data-testid="upload-batch-status">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: hasFailures ? '#A53636' : C.ink }}>
              {batch.active ? '本批正在上传' : hasFailures ? '本批有文件上传失败' : '本批上传完成'}
            </span>
            <span style={{ fontFamily: C.mono, fontSize: 12, color: C.ink3, fontVariantNumeric: 'tabular-nums' }}>
              {batch.completedFileCount || 0} / {batch.pictureNum}
            </span>
          </div>
          <div style={{ height: 6, background: C.hair, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${batchPercent}%`, height: '100%', background: hasFailures ? '#D96C5C' : C.brand, borderRadius: 3, transition: 'width .25s ease' }}/>
          </div>
          <div style={{ marginTop: 7, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11.5, color: C.ink3 }}>
            {batch.active && <span>处理中 1 · 等待 {Math.max(0, batch.pictureNum - (batch.completedFileCount || 0) - 1)}</span>}
            <span style={{ color: '#3F8F5F' }}>成功 {batch.successFileCount || 0}</span>
            {(batch.existFileCount || 0) > 0 && <span>已存在 {batch.existFileCount}</span>}
            <span style={{ color: hasFailures ? '#A53636' : C.ink3 }}>失败 {batch.failFileCount || 0}</span>
            {hasFailures && batch.failedFileNames?.length > 0 && (
              <span title={batch.failedFileNames.join('、')} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#A53636' }}>
                {batch.failedFileNames.join('、')}
              </span>
            )}
            {hasFailures && batch.failedFiles?.length > 0 && (
              <button data-testid="retry-failed-uploads" onClick={onRetry} style={{
                marginLeft: 'auto', padding: '5px 10px', borderRadius: 7,
                border: `1px solid ${C.brand}`, background: C.brandSoft, color: C.brand2,
                fontFamily: C.sans, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
              }}>重试失败项</button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function AiQualityScanButton({ onClick, scanStatus, scanCompleted, scanTotal, photoCount, browserSupported, busy }) {
  const isRunning = scanStatus === 'running';
  const disabled = !browserSupported || !photoCount || isRunning || busy;
  const label = isRunning
    ? `AI 扫描 ${scanCompleted}/${scanTotal}`
    : scanStatus === 'complete' ? '重新 AI 初筛' : 'AI 初筛';
  const title = !browserSupported
    ? '当前功能仅支持最新版 Chrome 或 Edge'
    : !photoCount ? '请先上传照片' : '扫描当前订单的全部照片';

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button data-testid="ai-quality-scan-order" onClick={onClick} disabled={disabled} title={title} style={{
        padding: 1.5, border: 'none', borderRadius: 10,
        background: disabled
          ? C.hair2
          : 'linear-gradient(115deg, #F777B0 0%, #A980F4 25%, #62C8F2 48%, #69D6A4 70%, #F2B45F 88%, #F777B0 100%)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : '0 8px 20px -13px rgba(126,92,210,.9)',
        transition: 'transform .28s cubic-bezier(.22,.8,.22,1), box-shadow .28s cubic-bezier(.22,.8,.22,1)',
      }}>
        <span style={{
          minHeight: 30, padding: '0 12px', borderRadius: 8.5,
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: disabled ? C.hair2 : C.ink, color: '#fff',
          fontFamily: C.sans, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2.8c.45 4.8 2.9 7.25 7.7 7.7-4.8.45-7.25 2.9-7.7 7.7-.45-4.8-2.9-7.25-7.7-7.7 4.8-.45 7.25-2.9 7.7-7.7Z" fill="url(#aiSpark)"/>
            <path d="M19.1 15.4c.17 1.8 1.1 2.73 2.9 2.9-1.8.17-2.73 1.1-2.9 2.9-.17-1.8-1.1-2.73-2.9-2.9 1.8-.17 2.73-1.1 2.9-2.9Z" fill="#F2B45F"/>
            <defs><linearGradient id="aiSpark" x1="4" y1="4" x2="19" y2="18" gradientUnits="userSpaceOnUse"><stop stopColor="#F777B0"/><stop offset=".48" stopColor="#A980F4"/><stop offset="1" stopColor="#62C8F2"/></linearGradient></defs>
          </svg>
          {label}
        </span>
      </button>
      <span data-testid="ai-quality-new-flag" style={{
        position: 'absolute', top: -7, right: -7, pointerEvents: 'none',
        padding: '2px 5px', borderRadius: 6,
        background: C.brand, color: '#fff', border: '1.5px solid #fff',
        fontFamily: C.sans, fontSize: 7.5, lineHeight: 1.2, fontWeight: 700,
        letterSpacing: .7, boxShadow: '0 3px 8px rgba(217,79,146,.28)',
      }}>NEW</span>
    </span>
  );
}

function AiQualityStatus({ results, failures, filter, onFilter, scanStatus, scanTotal, scanCompleted, photoCount }) {
  if (scanStatus === 'idle') return null;
  const summary = summarizeOrderQuality(results, failures);
  const isRunning = scanStatus === 'running';
  const isComplete = scanStatus === 'complete' && scanCompleted > 0;
  const progress = scanTotal ? Math.round((scanCompleted / scanTotal) * 100) : 0;
  const filters = [
    ['all', '全部照片', photoCount],
    ['review', '建议复核', summary.review],
    ['closed', '闭眼', summary.closed],
    ['blur', '模糊', summary.blur],
    ['exposure', '曝光', summary.exposure],
    ['duplicate', '近似', summary.duplicate],
  ];
  const verdictColor = summary.tone === 'good' ? '#2F7650' : summary.tone === 'warning' ? '#A14D37' : C.ink;

  return (
    <section data-testid="ai-quality-panel" style={{
      padding: '14px 36px', borderBottom: `1px solid ${C.hair}`,
      background: '#FFFAFC', flexShrink: 0,
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(230px, .75fr) minmax(360px, 1.25fr)',
        alignItems: 'center', gap: 20, padding: '14px 16px',
        background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 13,
        boxShadow: '0 10px 28px -26px rgba(61,54,50,.55)',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: C.brand2, fontFamily: C.sans, fontSize: 10, fontWeight: 600, letterSpacing: 1.4, textTransform: 'uppercase' }}>
            AI 品质初筛 · 本机处理
          </div>
          <div data-testid="ai-quality-verdict" style={{ marginTop: 5, color: isComplete ? verdictColor : C.ink, fontFamily: C.serif, fontSize: 19, lineHeight: 1.15 }}>
            {isRunning ? `正在扫描 ${scanCompleted} / ${scanTotal} 张` : isComplete ? summary.verdict : '一键检查整个订单'}
          </div>
          <div style={{ marginTop: 5, color: C.ink3, fontSize: 11.5, lineHeight: 1.5 }}>
            {isComplete
              ? `完成 ${summary.total} 张${failures ? `，${failures} 张未完成` : ''} · 结果仅作人工复核建议`
              : `扫描当前订单 ${photoCount} 张预览图，识别闭眼、模糊、曝光与近似照片`}
          </div>
        </div>

        <div>
          {isRunning ? (
            <div data-testid="ai-quality-progress">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7, color: C.ink3, fontFamily: C.mono, fontSize: 10.5 }}>
                <span>分析进度</span><span>{progress}%</span>
              </div>
              <div style={{ height: 5, background: C.hair, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: C.brand, borderRadius: 3, transition: 'width .4s cubic-bezier(.22,.8,.22,1)' }}/>
              </div>
            </div>
          ) : isComplete ? (
            <div data-testid="ai-quality-status" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {filters.map(([key, label, count]) => {
                const active = filter === key;
                return (
                  <button key={key} data-testid={`ai-quality-filter-${key}`} onClick={() => onFilter(key)} style={{
                    padding: '6px 9px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${active ? C.brand : C.hair2}`,
                    background: active ? '#FFF0F7' : '#fff', color: active ? C.brand2 : C.ink2,
                    fontFamily: C.sans, fontSize: 11.5, fontWeight: active ? 600 : 400,
                    transition: 'transform .25s cubic-bezier(.22,.8,.22,1)',
                  }}>{label} <span style={{ fontFamily: C.mono }}>{count}</span></button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function PeopleScanButton({ onClick, scanStatus, scanCompleted, scanTotal, photoCount, browserSupported, busy, quota, quotaChecking }) {
  const isRunning = scanStatus === 'running';
  const quotaExhausted = quota?.unlimited === false && quota.remaining <= 0;
  const disabled = !browserSupported || !photoCount || isRunning || busy || quotaChecking || quotaExhausted;
  const label = isRunning
    ? `人物 ${scanCompleted}/${scanTotal}`
    : quotaChecking ? '检查额度…'
      : scanStatus === 'complete' || scanStatus === 'ready' ? '重新人物分类' : '人物分类';
  const quotaLabel = quota?.unlimited ? '不限次数' : quota ? `剩余 ${quota.remaining}/${quota.limit}` : null;
  const title = quotaExhausted
    ? '本月 3 次人物分类额度已用完，高级会员不限次数'
    : quota?.unlimited ? '高级会员人物分类不限次数' : '按人物自动整理当前订单';
  return (
    <button data-testid="people-scan-order" onClick={onClick} disabled={disabled} title={title} style={{
      minHeight: 33, padding: '0 12px', borderRadius: 9,
      border: `1.5px solid ${disabled ? C.hair2 : '#A980F4'}`,
      background: disabled ? C.paper2 : 'linear-gradient(135deg, #FBF7FF, #F4FBFF)',
      color: disabled ? C.ink3 : '#6843A8', fontFamily: C.sans, fontSize: 12, fontWeight: 600,
      display: 'inline-flex', alignItems: 'center', gap: 7,
      cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
      boxShadow: disabled ? 'none' : '0 7px 18px -14px rgba(104,67,168,.8)',
    }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9" cy="8" r="3"/><path d="M3.5 19c.3-3.3 2.1-5 5.5-5s5.2 1.7 5.5 5"/><circle cx="17.3" cy="9" r="2.2"/><path d="M15.5 14.3c3.1-.7 5 .7 5.3 3.7"/>
      </svg>
      {label}
      {quotaLabel && <span data-testid="people-scan-quota" style={{ fontFamily: C.mono, fontSize: 9.5, opacity: .72 }}>{quotaLabel}</span>}
    </button>
  );
}

function PersonAvatar({ group, pictures }) {
  const picture = pictures.find(item => String(item.id) === String(group.representative?.pictureId));
  const bounds = group.representative?.bounds;
  const centerX = bounds ? ((bounds.left + bounds.right) / 2) * 100 : 50;
  const centerY = bounds ? ((bounds.top + bounds.bottom) / 2) * 100 : 50;
  return (
    <span style={{ width: 30, height: 30, borderRadius: 15, overflow: 'hidden', background: C.paper2, display: 'inline-flex', flexShrink: 0 }}>
      {picture && (
        <img src={getPictureThumbnailUrl(picture)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${centerX}% ${centerY}%` }}/>
      )}
    </span>
  );
}

function PersonGroupsPanel({ payload, status, completed, total, failures, pictures, activeId, onSelect, visibilitySaving, onVisibilityChange, saveFailed, onRetrySave, onReload }) {
  if (status === 'idle' || status === 'loading') return null;
  if (status === 'loadError') {
    return (
      <section data-testid="people-groups-load-error" style={{
        padding: '12px 36px', borderBottom: `1px solid ${C.hair}`,
        background: '#FFF7F4', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <span style={{ color: '#A53636', fontSize: 12 }}>人物分类结果加载失败</span>
        <button type="button" onClick={onReload} style={{
          padding: '5px 9px', borderRadius: 7, border: `1px solid ${C.hair2}`,
          background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 11.5, cursor: 'pointer',
        }}>重新加载</button>
      </section>
    );
  }
  const groups = payload?.groups || [];
  const isRunning = status === 'running';
  const progress = total ? Math.round((completed / total) * 100) : 0;
  return (
    <section data-testid="people-groups-panel" style={{
      padding: '12px 36px', borderBottom: `1px solid ${C.hair}`,
      background: '#FAF8FF', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 24, flexWrap: 'wrap', flexShrink: 0,
    }}>
      <div data-testid="people-groups-left" style={{
        flex: '1 1 560px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{ minWidth: 190, flexShrink: 0 }}>
          <div style={{ color: '#7652A8', fontSize: 10, fontWeight: 600, letterSpacing: 1.3, textTransform: 'uppercase' }}>AI 人物分类 · 本机识别</div>
          <div style={{ marginTop: 4, color: C.ink2, fontSize: 12 }}>
            {isRunning
              ? `正在分析 ${completed} / ${total} 张`
              : groups.length ? `识别到 ${groups.length} 位人物` : '未识别到人物'}
          </div>
        </div>
        {isRunning ? (
          <div data-testid="people-scan-progress" style={{ flex: 1, minWidth: 260 }}>
            <div style={{ height: 5, background: C.hair, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #A980F4, #62C8F2)', borderRadius: 3, transition: 'width .4s cubic-bezier(.22,.8,.22,1)' }}/>
            </div>
          </div>
        ) : (
          <div data-testid="people-group-filters" style={{
            flex: '1 1 auto', minWidth: 0, maxHeight: 82, display: 'flex', alignItems: 'flex-start', alignContent: 'flex-start',
            gap: 7, flexWrap: 'wrap', overflowX: 'hidden', overflowY: 'auto', padding: '2px 6px 2px 0',
            scrollbarGutter: 'stable',
          }}>
            <button data-testid="people-filter-all" onClick={() => onSelect('all')} style={{
              padding: '7px 10px', borderRadius: 18, border: `1px solid ${activeId === 'all' ? '#A980F4' : C.hair2}`,
              background: activeId === 'all' ? '#F0E9FF' : '#fff', color: activeId === 'all' ? '#6843A8' : C.ink2,
              fontFamily: C.sans, fontSize: 11.5, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>全部人物</button>
            {groups.map(group => (
              <button key={group.id} data-testid={`people-filter-${group.id}`} onClick={() => onSelect(group.id)} style={{
                padding: '3px 9px 3px 3px', borderRadius: 20, border: `1px solid ${activeId === group.id ? '#A980F4' : C.hair2}`,
                background: activeId === group.id ? '#F0E9FF' : '#fff', color: activeId === group.id ? '#6843A8' : C.ink2,
                display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: C.sans, fontSize: 11.5, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                <PersonAvatar group={group} pictures={pictures}/>
                <span>{group.label} · {group.photoCount}</span>
              </button>
            ))}
          </div>
        )}
        {!isRunning && saveFailed && (
          <button type="button" data-testid="people-save-retry" onClick={onRetrySave} style={{
            border: 'none', background: 'transparent', color: '#A53636', padding: 0,
            fontFamily: C.sans, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>保存失败，重新提交</button>
        )}
        {!isRunning && failures > 0 && <span style={{ color: '#A53636', fontSize: 11, whiteSpace: 'nowrap' }}>{failures} 张未完成</span>}
      </div>
      {!isRunning && groups.length > 0 && (
        <button
          type="button"
          role="switch"
          aria-checked={payload?.customerVisible === true}
          aria-label={payload?.customerVisible === true ? '关闭网页和小程序端人物分类展示' : '发布人物分类到网页和小程序端'}
          aria-busy={visibilitySaving}
          data-testid="people-customer-visibility"
          disabled={visibilitySaving}
          onClick={() => onVisibilityChange(!(payload?.customerVisible === true))}
          style={{
            flex: '0 0 auto', minWidth: 252, padding: 4, borderRadius: 15,
            border: `1.5px solid ${payload?.customerVisible === true ? '#8D63D2' : '#D6C6EF'}`,
            background: payload?.customerVisible === true
              ? 'linear-gradient(135deg, #EDE3FF 0%, #F5EDFF 48%, #E8F8FF 100%)'
              : 'linear-gradient(135deg, #FBF8FF 0%, #F7F3FD 100%)',
            boxShadow: payload?.customerVisible === true
              ? '0 10px 24px -14px rgba(104,67,168,.72), inset 0 1px 0 rgba(255,255,255,.8)'
              : '0 6px 18px -16px rgba(104,67,168,.45), inset 0 1px 0 rgba(255,255,255,.9)',
            color: C.ink2, fontFamily: C.sans,
            cursor: visibilitySaving ? 'wait' : 'pointer',
            opacity: visibilitySaving ? .7 : 1,
            transition: 'transform .3s cubic-bezier(.22,.8,.22,1), box-shadow .3s cubic-bezier(.22,.8,.22,1), border-color .3s cubic-bezier(.22,.8,.22,1)',
          }}
        >
          <span style={{
            width: '100%', minHeight: 58, padding: '9px 11px 9px 12px', boxSizing: 'border-box', borderRadius: 11,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
            background: 'rgba(255,255,255,.82)', boxShadow: 'inset 0 1px 0 #fff',
          }}>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5, minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: payload?.customerVisible === true ? '#38A169' : C.ink3,
                  boxShadow: payload?.customerVisible === true ? '0 0 0 4px rgba(56,161,105,.13)' : 'none',
                }}/>
                <span style={{ color: C.ink, fontSize: 12.5, fontWeight: 650, whiteSpace: 'nowrap' }}>
                  {visibilitySaving ? '正在保存发布设置…' : '发布到客户选图端'}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {['网页', '小程序'].map(channel => (
                  <span key={channel} style={{
                    padding: '2px 7px', borderRadius: 8, fontFamily: C.mono, fontSize: 9.5,
                    color: payload?.customerVisible === true ? '#6843A8' : C.ink3,
                    background: payload?.customerVisible === true ? '#F0E8FF' : C.paper2,
                  }}>{channel}</span>
                ))}
                <span data-testid="people-customer-visibility-status" style={{
                  marginLeft: 2, color: payload?.customerVisible === true ? '#2F855A' : C.ink3,
                  fontSize: 10.5, fontWeight: 600,
                }}>
                  {payload?.customerVisible === true ? '已展示' : '未展示'}
                </span>
              </span>
            </span>
            <span aria-hidden="true" style={{
              width: 48, height: 26, borderRadius: 14, padding: 3, boxSizing: 'border-box', flexShrink: 0,
              display: 'inline-flex', justifyContent: payload?.customerVisible === true ? 'flex-end' : 'flex-start',
              background: payload?.customerVisible === true ? 'linear-gradient(135deg, #7652A8, #A980F4)' : '#CDC4D8',
              boxShadow: payload?.customerVisible === true ? '0 5px 12px -6px rgba(104,67,168,.9)' : 'inset 0 1px 2px rgba(0,0,0,.12)',
              transition: 'background .3s cubic-bezier(.22,.8,.22,1)',
            }}>
              <span style={{
                width: 20, height: 20, borderRadius: '50%', background: '#fff',
                boxShadow: '0 2px 7px rgba(50,35,70,.24)',
                transition: 'transform .3s cubic-bezier(.22,.8,.22,1)',
              }}/>
            </span>
          </span>
        </button>
      )}
    </section>
  );
}

// ── Step3 ──────────────────────────────────────────────────────────

const Step3 = inject('pictureStore', 'snackbarStore', 'dialogStore', 'ordersStore')(observer(({ pictureStore, snackbarStore, dialogStore, ordersStore }) => {
  const { orderId } = useParams();
  const [tabVal, setTabVal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [cols, setCols] = useState(6);
  const [uploadBatch, setUploadBatch] = useState(null);
  const [aiQualityFilter, setAiQualityFilter] = useState('all');
  const [aiQualityResults, setAiQualityResults] = useState([]);
  const [aiQualityFailures, setAiQualityFailures] = useState(0);
  const [aiQualityScanStatus, setAiQualityScanStatus] = useState('idle');
  const [aiQualityScanTotal, setAiQualityScanTotal] = useState(0);
  const [aiQualityScanCompleted, setAiQualityScanCompleted] = useState(0);
  const aiQualitySessionRef = React.useRef(0);
  const [personGroupsPayload, setPersonGroupsPayload] = useState(null);
  const [personSaveFailed, setPersonSaveFailed] = useState(false);
  const [personVisibilitySaving, setPersonVisibilitySaving] = useState(false);
  const [personReloadKey, setPersonReloadKey] = useState(0);
  const [personScanStatus, setPersonScanStatus] = useState('loading');
  const [personScanTotal, setPersonScanTotal] = useState(0);
  const [personScanCompleted, setPersonScanCompleted] = useState(0);
  const [personScanFailures, setPersonScanFailures] = useState(0);
  const [personScanQuota, setPersonScanQuota] = useState(null);
  const [personQuotaChecking, setPersonQuotaChecking] = useState(false);
  const [personScanReservationId, setPersonScanReservationId] = useState(null);
  const [activePersonId, setActivePersonId] = useState('all');
  const personScanSessionRef = React.useRef(0);
  const personQuotaReserveRef = React.useRef(false);
  const uploaderRef = React.useRef(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'aggregate'
  const [coverMode, setCoverMode] = useState(false);
  const [pendingCover, setPendingCover] = useState(null);
  const { t } = useTranslation();

  const isAggregate = viewMode === 'aggregate';
  const order = ordersStore.currentOrder || {};
  const hasSelections = hasOrderSelections(order);

  const handleSaveCover = () => {
    if (pendingCover && order.Album?.id) {
      http.put(`/user/albums/${order.Album.id}/setAsCover`, { cover: pendingCover }).then(() => {
        pictureStore.setCover(pendingCover);
        setCoverMode(false);
        setPendingCover(null);
      });
    }
  };

  // Build selectedBy map with each customer's normalized payment status.
  const selectedByMap = useMemo(() => {
    if (!isAggregate) return {};
    const map = {};
    pictureStore.selections.forEach((sel, i) => {
      (sel.pictureIds || []).forEach(pid => {
        if (!map[pid]) map[pid] = [];
        map[pid].push({
          id: sel.id,
          customerName: sel.customerName,
          initial: sel.customerName?.charAt(0) || '?',
          color: CLIENT_COLORS[i % CLIENT_COLORS.length],
          paymentStatus: getPaymentStatus(sel),
        });
      });
    });
    return map;
  }, [isAggregate, pictureStore.selections]);

  const handleTab = (idx) => {
    setTabVal(idx);
    switch (idx) {
      case 0: pictureStore.setPreviewPicturesFilter('all'); break;
      case 1: pictureStore.setPreviewPicturesFilter('selected'); break;
      case 2: pictureStore.setPreviewPicturesFilter('unselected'); break;
    }
  };

  const handleDelete = () => {
    dialogStore.openDialog('Are you sure you want to delete those pictures?', () => {
      const selectedIds = new Set(pictureStore.multiSelections.map(String));
      trackQualityDeletion(aiQualityResults.filter(result => selectedIds.has(String(result.pictureId))));
      pictureStore.deletePictrus(orderId, pictureStore.multiSelections);
    });
  };

  const handleSelectClient = async (selId) => {
    setViewMode('single');
    setSelectionLoading(true);
    pictureStore.setSelectionId(selId);
    await new Promise(r => setTimeout(r, 300));
    setSelectionLoading(false);
  };

  useEffect(() => {
    if (!ordersStore.currentOrder) return;
    pictureStore.reset();
    const pictures = ordersStore.currentPictures || [];
    const configuredCover = ordersStore.currentOrder.Album.cover;
    const coverStillExists = pictures.some(picture => String(picture.id) === String(configuredCover));
    pictureStore.setCover(coverStillExists ? configuredCover : (pictures[0]?.id || null));
    pictureStore.setPictures(pictures);
    pictureStore.setSelections(ordersStore.currentSelections);
    pictureStore.setTags(ordersStore.currentTags);
  }, [ordersStore.currentOrder]);

  useEffect(() => { ordersStore.fetchOrder(orderId); }, [orderId]);

  useEffect(() => {
    aiQualitySessionRef.current += 1;
    setAiQualityFilter('all');
    setAiQualityResults([]);
    setAiQualityFailures(0);
    setAiQualityScanStatus('idle');
    setAiQualityScanTotal(0);
    setAiQualityScanCompleted(0);
  }, [orderId]);

  useEffect(() => {
    const sessionId = personScanSessionRef.current + 1;
    personScanSessionRef.current = sessionId;
    setPersonScanStatus('loading');
    setPersonGroupsPayload(null);
    setPersonSaveFailed(false);
    setPersonVisibilitySaving(false);
    setActivePersonId('all');
    setPersonScanTotal(0);
    setPersonScanCompleted(0);
    setPersonScanFailures(0);
    setPersonScanReservationId(null);
    loadPersonGroups(orderId).then(({ payload, quota, error }) => {
      if (sessionId !== personScanSessionRef.current) return;
      setPersonGroupsPayload(payload);
      setPersonScanQuota(quota || null);
      setPersonScanStatus(error ? 'loadError' : payload ? 'ready' : 'idle');
    });
  }, [orderId, personReloadKey]);

  // Counts — must be before early return (hooks rules)
  const aggregateCount = useMemo(() => {
    return (pictureStore.pictures || []).filter(p =>
      (pictureStore.selections || []).some(sel => (sel.pictureIds || []).includes(p.id))
    ).length;
  }, [pictureStore.pictures, pictureStore.selections]);

  const counts = isAggregate ? {
    all: (pictureStore.pictures || []).length,
    selected: aggregateCount,
    unselected: (pictureStore.pictures || []).length - aggregateCount,
  } : {
    all: (pictureStore.pictures || []).length,
    selected: pictureStore.selectedPictures?.length || 0,
    unselected: pictureStore.unSelectedPictures?.length || 0,
  };

  const photoTotal = order.Album?.pictureCount || 0;
  const photoSelected = order.selectedPhotoCount || 0;
  const uploadedCount = (pictureStore.pictures || []).filter(p => p.publicUrl || p.publicUrlSmall || p.publicUrlThumbnail).length;
  const aiQualityBrowserSupported = isAiQualityBrowserSupported();
  const activePictureIds = new Set((pictureStore.pictures || []).map(picture => String(picture.id)));
  const activeAiQualityResults = aiQualityResults.filter(result => activePictureIds.has(String(result.pictureId)));
  const aiQualityResultMap = new Map(
    activeAiQualityResults.map(result => [String(result.pictureId), result])
  );

  const handleUploadStart = (pictureNum) => {
    setUploadBatch({ pictureNum, completedFileCount: 0, successFileCount: 0, existFileCount: 0, failFileCount: 0, failedFileNames: [], active: true });
  };

  const handleUploadProgress = (progress) => setUploadBatch({ ...progress, active: true });

  const handleUploaded = (result) => {
    setUploadBatch({ ...result, completedFileCount: result.pictureNum, active: false });
    ordersStore.fetchOrder(orderId);
    if (result.failFileCount === 0) {
      const suffix = result.existFileCount > 0 ? `，${result.existFileCount} 张已存在` : '';
      snackbarStore.showSnackbar(`${result.successFileCount} 张照片上传成功${suffix}`, 'success');
    }
  };

  const handleRetryUploads = () => {
    const failedFiles = uploadBatch?.failedFiles || [];
    if (failedFiles.length) uploaderRef.current?.retryFiles(failedFiles);
  };

  const handleAiQualityFilter = (filter) => {
    setAiQualityFilter(filter);
    trackQualityFilterViewed(filter);
  };

  const handleOrderQualityScan = async () => {
    if (aiQualityScanStatus === 'running') return;
    const candidates = (pictureStore.pictures || []).filter(picture => (
      picture?.id
      && (picture.publicUrlSmall || picture.publicUrlThumbnail || picture.publicUrl)
      && !(typeof picture.progress === 'number' && !picture.publicUrl)
    ));
    const sessionId = aiQualitySessionRef.current + 1;
    aiQualitySessionRef.current = sessionId;
    setAiQualityFilter('all');
    setAiQualityResults([]);
    setAiQualityFailures(0);
    setAiQualityScanTotal(candidates.length);
    setAiQualityScanCompleted(0);
    setAiQualityScanStatus(candidates.length ? 'running' : 'complete');
    trackOrderScanStarted();

    let collectedResults = [];
    let failureCount = 0;
    for (let index = 0; index < candidates.length; index += 1) {
      if (sessionId !== aiQualitySessionRef.current) return;
      const picture = candidates[index];
      try {
        const file = await downloadPictureForAnalysis(picture);
        const result = await enqueuePhotoQualityAnalysis(file);
        if (sessionId !== aiQualitySessionRef.current) return;
        const qualityResult = {
          ...result,
          id: `quality-${picture.id}`,
          pictureId: picture.id,
          name: picture.originalFileName || picture.fileName || file.name,
          previewFile: undefined,
        };
        const duplicateIds = new Set(collectedResults.filter(item => item.isDuplicateAlternative).map(item => item.pictureId));
        collectedResults = [...collectedResults, qualityResult];
        groupNearDuplicates(collectedResults);
        if (collectedResults.some(item => item.isDuplicateAlternative && !duplicateIds.has(item.pictureId))) trackDuplicateDetected();
        trackQualityResult(qualityResult);
        setAiQualityResults([...collectedResults]);
      } catch (error) {
        if (sessionId !== aiQualitySessionRef.current) return;
        failureCount += 1;
        setAiQualityFailures(failureCount);
        trackQualityFailure();
      } finally {
        if (sessionId === aiQualitySessionRef.current) setAiQualityScanCompleted(index + 1);
      }
    }

    if (sessionId !== aiQualitySessionRef.current) return;
    setAiQualityScanStatus('complete');
    trackOrderScanCompleted();
    const summary = summarizeOrderQuality(collectedResults, failureCount);
    if (summary.total || summary.failures) {
      snackbarStore.showSnackbar(
        summary.review ? `AI 初筛完成，${summary.review} 张建议复核` : 'AI 初筛完成，未发现明显问题',
        summary.review ? 'warning' : 'success'
      );
    }
  };

  const handlePersonScan = async () => {
    if (personScanStatus === 'running' || aiQualityScanStatus === 'running' || personQuotaReserveRef.current) return;
    const candidates = (pictureStore.pictures || []).filter(picture => (
      picture?.id && (picture.publicUrlSmall || picture.publicUrlThumbnail || picture.publicUrl)
    ));
    if (!candidates.length) return;
    personQuotaReserveRef.current = true;
    setPersonQuotaChecking(true);
    let scanReservationId = null;
    try {
      const { quota, reservationId } = await consumePersonScan(orderId);
      scanReservationId = reservationId;
      setPersonScanQuota(quota);
      setPersonScanReservationId(reservationId);
    } catch (error) {
      if (error?.quota) setPersonScanQuota(error.quota);
      snackbarStore.showSnackbar(
        error?.code === 'AI_PERSON_SCAN_MONTHLY_LIMIT_REACHED'
          ? '本月 3 次人物分类额度已用完，高级会员可无限使用'
          : '暂时无法确认人物分类额度，请稍后重试',
        error?.code === 'AI_PERSON_SCAN_MONTHLY_LIMIT_REACHED' ? 'warning' : 'error'
      );
      return;
    } finally {
      personQuotaReserveRef.current = false;
      setPersonQuotaChecking(false);
    }
    const sessionId = personScanSessionRef.current + 1;
    personScanSessionRef.current = sessionId;
    setActivePersonId('all');
    setPersonGroupsPayload(null);
    setPersonSaveFailed(false);
    setPersonScanStatus(candidates.length ? 'running' : 'complete');
    setPersonScanTotal(candidates.length);
    setPersonScanCompleted(0);
    setPersonScanFailures(0);

    let allFaces = [];
    let detectedFaceCount = 0;
    let skippedSmallFaces = 0;
    let failureCount = 0;
    for (let index = 0; index < candidates.length; index += 1) {
      if (sessionId !== personScanSessionRef.current) return;
      try {
        const result = await scanPicturePeople(candidates[index]);
        if (sessionId !== personScanSessionRef.current) return;
        allFaces = [...allFaces, ...result.faces];
        detectedFaceCount += result.detectedFaces;
        skippedSmallFaces += result.skippedSmallFaces;
      } catch (error) {
        if (sessionId !== personScanSessionRef.current) return;
        failureCount += 1;
        setPersonScanFailures(failureCount);
      } finally {
        if (sessionId === personScanSessionRef.current) setPersonScanCompleted(index + 1);
      }
    }

    if (sessionId !== personScanSessionRef.current) return;
    const clusters = clusterFaces(allFaces);
    const payload = serializePersonGroups(clusters, {
      pictureCount: candidates.length,
      faceCount: detectedFaceCount,
      classifiedFaceCount: allFaces.length,
      skippedSmallFaces,
      failures: failureCount,
      customerVisible: personGroupsPayload?.customerVisible === true,
    });
    if (sessionId !== personScanSessionRef.current) return;
    setPersonGroupsPayload(payload);
    setPersonScanStatus('complete');
    try {
      await savePersonGroups(orderId, payload, { reservationId: scanReservationId });
      if (sessionId !== personScanSessionRef.current) return;
      setPersonScanReservationId(null);
      snackbarStore.showSnackbar(
        payload.groups.length ? `人物分类完成，识别到 ${payload.groups.length} 位人物` : '人物分类完成，未识别到人物',
        payload.groups.length ? 'success' : 'info'
      );
    } catch (error) {
      if (sessionId !== personScanSessionRef.current) return;
      setPersonSaveFailed(true);
      snackbarStore.showSnackbar('人物分类已完成，但结果提交失败', 'error');
    }
  };

  const handleRetryPersonSave = async () => {
    if (!personGroupsPayload) return;
    try {
      await savePersonGroups(orderId, personGroupsPayload, { reservationId: personScanReservationId });
      setPersonSaveFailed(false);
      setPersonScanReservationId(null);
      snackbarStore.showSnackbar('人物分类结果已提交', 'success');
    } catch (error) {
      snackbarStore.showSnackbar('人物分类结果提交失败', 'error');
    }
  };

  const handlePersonVisibilityChange = async (customerVisible) => {
    if (!personGroupsPayload || personVisibilitySaving) return;
    const previousPayload = personGroupsPayload;
    const nextPayload = { ...previousPayload, customerVisible };
    setPersonGroupsPayload(nextPayload);
    setPersonVisibilitySaving(true);
    setPersonSaveFailed(false);
    try {
      await savePersonGroups(orderId, nextPayload);
      snackbarStore.showSnackbar(customerVisible ? '客户选图端将展示人物分类' : '客户选图端已隐藏人物分类', 'success');
    } catch (error) {
      setPersonGroupsPayload(previousPayload);
      setPersonSaveFailed(true);
      snackbarStore.showSnackbar('展示设置保存失败', 'error');
    } finally {
      setPersonVisibilitySaving(false);
    }
  };

  useEffect(() => {
    if (!uploadBatch?.failedFiles?.length) return undefined;
    const warnBeforeLeave = (event) => {
      event.preventDefault();
      event.returnValue = '还有照片上传失败，离开后需要重新选择文件。';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', warnBeforeLeave);
    return () => window.removeEventListener('beforeunload', warnBeforeLeave);
  }, [uploadBatch?.failedFiles?.length]);

  if (!ordersStore.currentOrder) return <div style={{ padding: 40, color: C.ink3 }}>Loading...</div>;

  // Which photo list to use
  const allPhotos = isAggregate ? pictureStore.pictures : pictureStore.previewPictures;
  const filteredPhotos = allPhotos?.filter(photo => {
    if (searchText && !(photo.fileName || photo.originalFileName || '').includes(searchText)) return false;
    if (aiQualityFilter !== 'all') {
      const result = aiQualityResultMap.get(String(photo.id));
      if (!result) return false;
      if (aiQualityFilter === 'review' && !(result.faceAnalysis?.closedEyeFaces > 0 || result.sharpness?.isBlurry || Boolean(result.exposure?.issue) || result.isDuplicateAlternative)) return false;
      if (aiQualityFilter === 'closed' && !(result.faceAnalysis?.closedEyeFaces > 0)) return false;
      if (aiQualityFilter === 'blur' && !result.sharpness?.isBlurry) return false;
      if (aiQualityFilter === 'exposure' && !result.exposure?.issue) return false;
      if (aiQualityFilter === 'duplicate' && !result.isDuplicateAlternative) return false;
    }
    if (activePersonId !== 'all') {
      const personGroup = personGroupsPayload?.groups?.find(group => group.id === activePersonId);
      if (!personGroup?.pictureIds?.some(pictureId => String(pictureId) === String(photo.id))) return false;
    }
    return true;
  }) || [];

  const multi = pictureStore.multiMode;
  const multiCount = pictureStore.multiSelections.length;

  return (
    <StepShell order={order} photoCount={order.photoCount} photoSelected={photoSelected} photoTotal={photoTotal}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* ── StageHeader ── */}
        <header style={{
          padding: '28px 36px 22px',
          borderBottom: `1px solid ${C.hair}`,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 24, background: C.paper, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 6 }}>
              Step 03 · Library
            </div>
            <h2 style={{ fontFamily: C.serif, fontSize: 30, lineHeight: 1.05, margin: '0 0 8px', fontWeight: 400, color: C.ink, letterSpacing: -0.4 }}>
              {photoTotal} 张照片
            </h2>
            <p style={{ margin: 0, fontSize: 12.5, color: C.ink3, lineHeight: 1.5 }}>
              拖入新照片即可上传。多选模式下可批量删除。
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <AiQualityScanButton
              onClick={handleOrderQualityScan}
              scanStatus={aiQualityScanStatus}
              scanCompleted={aiQualityScanCompleted}
              scanTotal={aiQualityScanTotal}
              photoCount={uploadedCount}
              browserSupported={aiQualityBrowserSupported}
              busy={personScanStatus === 'running'}
            />
            <PeopleScanButton
              onClick={handlePersonScan}
              scanStatus={personScanStatus}
              scanCompleted={personScanCompleted}
              scanTotal={personScanTotal}
              photoCount={uploadedCount}
              browserSupported={aiQualityBrowserSupported}
              busy={aiQualityScanStatus === 'running'}
              quota={personScanQuota}
              quotaChecking={personQuotaChecking}
            />
            <FileUploader ref={uploaderRef} size="small" albumId={order.Album?.id}
              openWatermark={order.openWatermark} watermarkStyle={order.watermarkStyle}
              watermark={order.watermark} watermarkAlpha={order.watermarkAlpha}
              watermarkPosition={order.watermarkPosition} watermarkFontSize={order.watermarkFontSize}
              onStartUpload={handleUploadStart}
              onUploadProgress={handleUploadProgress}
              onUploaded={handleUploaded}
            />
          </div>
        </header>

        {hasSelections && (
          <div data-testid="photo-library-advisory" style={{
            padding: '12px 36px',
            background: C.warningBg, color: C.warningFg,
            borderBottom: `1px solid ${C.warningDot}`,
            fontSize: 12.5, lineHeight: 1.55,
          }}>
            <strong>已有 {Math.max(1, Number(order.selectionNum || 0))} 份客户选图记录。</strong>
            你仍可管理照片，但删除或替换客户已选照片可能让现有记录难以核对，请谨慎操作。
          </div>
        )}

        <UploadStatus uploadedCount={uploadedCount} batch={uploadBatch} onRetry={handleRetryUploads}/>
        <AiQualityStatus
          results={activeAiQualityResults}
          failures={aiQualityFailures}
          filter={aiQualityFilter}
          onFilter={handleAiQualityFilter}
          scanStatus={aiQualityScanStatus}
          scanTotal={aiQualityScanTotal}
          scanCompleted={aiQualityScanCompleted}
          photoCount={uploadedCount}
        />
        <PersonGroupsPanel
          payload={personGroupsPayload}
          status={personScanStatus}
          completed={personScanCompleted}
          total={personScanTotal}
          failures={personScanFailures}
          pictures={pictureStore.pictures || []}
          activeId={activePersonId}
          onSelect={setActivePersonId}
          visibilitySaving={personVisibilitySaving}
          onVisibilityChange={handlePersonVisibilityChange}
          saveFailed={personSaveFailed}
          onRetrySave={handleRetryPersonSave}
          onReload={() => setPersonReloadKey(value => value + 1)}
        />

        {/* Client selector strip removed — selection records live in step5 */}

        {/* ── TOOLBAR ── */}
        <div style={{
          padding: '14px 44px', borderBottom: `1px solid ${C.hair}`,
          display: 'flex', alignItems: 'center', gap: 14, background: C.paper2, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', gap: 2, padding: 3, background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 7 }}>
            {TAB_OPTS.map(([k, l], i) => (
              <button key={k} onClick={() => handleTab(i)} style={{
                padding: '6px 14px', border: 'none', borderRadius: 5, cursor: 'pointer',
                background: tabVal === i ? C.ink : 'transparent',
                color: tabVal === i ? '#fff' : C.ink2,
                fontFamily: C.sans, fontSize: 13, fontWeight: 500,
              }}>{l} <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums', marginLeft: 4 }}>{counts[k]}</span></button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {pictureStore.tags.map(tag => {
              const on = pictureStore.haveTagFilter(tag.id);
              return (
                <button key={tag.id} onClick={() => on ? pictureStore.removeTagFilter(tag.id) : pictureStore.addTagFilter(tag.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 10px', border: `1px solid ${on ? tag.color : C.hair2}`, borderRadius: 12,
                    background: on ? tag.color : '#fff', color: on ? '#fff' : C.ink2,
                    fontFamily: C.sans, fontSize: 12, cursor: 'pointer', transition: 'all .15s',
                  }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: on ? '#fff' : tag.color }}/>{tag.name}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, maxWidth: 280, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', border: `1px solid ${C.hair2}`, borderRadius: 7, background: '#fff' }}>
            <Icon name="search" size={14}/>
            <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="搜索文件名…"
              style={{ flex: 1, border: 'none', outline: 'none', fontFamily: C.sans, fontSize: 13, background: 'transparent', color: C.ink }}/>
            {searchText && <button onClick={() => setSearchText('')} style={{ border: 'none', background: 'none', color: C.ink3, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>}
          </div>

          <div style={{ display: 'flex', gap: 2, padding: 3, background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 7 }}>
            {DENSITIES.map(d => <DensityBtn key={d} cols={cols} v={d} setV={setCols}/>)}
          </div>

          {!multi && (
            <button onClick={() => { setCoverMode(!coverMode); setPendingCover(null); }} style={{
              padding: '7px 14px', border: `1px solid ${coverMode ? C.brand : C.hair2}`, borderRadius: 7,
              background: coverMode ? C.brandSoft : '#fff',
              color: coverMode ? C.brand2 : C.ink2,
              fontFamily: C.sans, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill={coverMode ? C.brand : 'none'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              {coverMode ? '取消' : '设封面'}
            </button>
          )}
          {!coverMode && (
            <button onClick={() => pictureStore.setMultiMode(!multi)} style={{
              padding: '7px 14px', border: `1px solid ${C.hair2}`, borderRadius: 7,
              background: multi ? C.ink : '#fff', color: multi ? '#fff' : C.ink,
              fontFamily: C.sans, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}>{multi ? '退出多选' : '多选'}</button>
          )}
        </div>

        {/* ── COVER BAR ── */}
        {coverMode && (
          <div style={{
            padding: '12px 44px', background: C.brandSoft,
            borderBottom: `1px solid ${C.hair}`,
            display: 'flex', alignItems: 'center', gap: 14,
            fontSize: 13, flexShrink: 0,
          }}>
            <span style={{ color: C.brand2, fontWeight: 500 }}>
              {pendingCover ? '已选择一张照片作为封面' : '点击一张照片设为封面'}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={() => { setCoverMode(false); setPendingCover(null); }} style={{
                padding: '7px 14px', borderRadius: 7, border: `1px solid ${C.hair2}`,
                background: '#fff', color: C.ink, fontFamily: C.sans, fontSize: 13, cursor: 'pointer',
              }}>取消</button>
              <button onClick={handleSaveCover} disabled={!pendingCover} style={{
                padding: '7px 14px', borderRadius: 7, border: 'none',
                background: pendingCover ? C.brand : C.hair2,
                color: '#fff', fontFamily: C.sans, fontSize: 13, fontWeight: 500, cursor: pendingCover ? 'pointer' : 'not-allowed',
                boxShadow: pendingCover ? `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)` : 'none',
              }}>保存封面</button>
            </div>
          </div>
        )}

        {/* ── BULK BAR ── */}
        {multi && multiCount > 0 && (
          <div style={{ padding: '12px 44px', background: C.ink, color: '#fff', display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, flexShrink: 0 }}>
            <span style={{ fontWeight: 500 }}>已选中 {multiCount} 张</span>
            <span style={{ width: 1, height: 16, background: 'rgba(255,255,255,.2)' }}/>
            <button onClick={handleDelete} style={{ background: 'transparent', border: 'none', color: '#ff8c8c', fontFamily: C.sans, fontSize: 13, cursor: 'pointer', padding: '4px 8px', borderRadius: 4 }}>✕ 删除</button>
            <button onClick={() => pictureStore.setMultiMode(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontFamily: C.sans, fontSize: 13, cursor: 'pointer', padding: '4px 8px', borderRadius: 4, marginLeft: 'auto' }}>取消</button>
          </div>
        )}

        {/* ── GRID ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 44px 64px' }}>
          {!selectionLoading && filteredPhotos.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
              {filteredPhotos.map(pic => (
                <PhotoTile key={pic.id}
                  orderId={orderId} albumId={order.Album?.id}
                  picture={pic} cols={cols} multiMode={multi}
                  isCover={pictureStore.cover === pic.id}
                  pictureStore={pictureStore} albumClaimed={pictureStore.albumClaimed}
                  aggregate={isAggregate}
                  selectedBy={selectedByMap[pic.id] || []}
                  coverMode={coverMode}
                  pendingCover={pendingCover}
                  onCoverSelect={setPendingCover}
                  qualityResult={aiQualityResultMap.get(String(pic.id))}
                />
              ))}
            </div>
          ) : (
            !selectionLoading && <Empty message={t("No picture available.")}>
              <FileUploader size="large" albumId={order.Album?.id}
                openWatermark={order.openWatermark} watermarkStyle={order.watermarkStyle}
                watermark={order.watermark} watermarkAlpha={order.watermarkAlpha}
                watermarkPosition={order.watermarkPosition} watermarkFontSize={order.watermarkFontSize}
                onStartUpload={handleUploadStart}
                onUploadProgress={handleUploadProgress}
                onUploaded={handleUploaded}
              />
            </Empty>
          )}
        </div>

      </div>
      <ImageDialog pictureStore={pictureStore}/>
    </StepShell>
  );
}));

export default withAuth(Step3);
